/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { AdvancedFilters } from "../../models/http/dashboard/advanced-filters.model";
import { KPI } from "../../models/http/dashboard/kpi.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { CommunicationService } from "../communication.service";

import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";

@Injectable({
	providedIn: "root"
})
export class DashboardEnergyHttpService {
	private _dashboardEnergyOperations = ENDPOINT_GROUPS.dashboardEnergy.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Fetches the worst 10 layers based on the provided parameters.
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An observable containing the response from the service call.
	 */
	public getWorst10Layers(filters: AdvancedFilters): Observable<ChartResponse<BarChart>> {
		return this._communicationService
			.callService(
				this._dashboardEnergyOperations.getWorst10Layers.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the worst 10 applications based on energy consumption within a specified date range.
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An observable containing the response from the server with the worst 10 applications.
	 */
	public getWorst10Apps(filters: AdvancedFilters): Observable<ChartResponse<BarChart>> {
		return this._communicationService
			.callService(
				this._dashboardEnergyOperations.getWorst10Apps.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Fetches the performance data of applications within a specified date range.
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An observable containing the performance data of the applications.
	 */
	public getAppsPerformance(filters: AdvancedFilters): Observable<ChartResponse<LineChart>> {
		return this._communicationService
			.callService(
				this._dashboardEnergyOperations.getAppsPerformance.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map((response: LineChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	public getTotalCO2(filters: AdvancedFilters): Observable<KPI> {
		return this._communicationService.callService(
			this._dashboardEnergyOperations.getTotalCo2.service,
			HttpMethods.POST,
			{},
			filters
		);
	}
}
